import { useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { setTimezone } from "../shared/format";
import { useOnline } from "../shared/live";
import { navigate, useRoute } from "../shared/router";
import { LoginScreen, Shell, type NavItem } from "../shared/shell";
import { Loading, ToastProvider } from "../shared/ui";
import { canOpen, home } from "./access";
import { api, Ctx, live, queue, type Me, type Refs } from "./ctx";
import { AdminPage } from "./pages/Admin";
import { CatalogsPage } from "./pages/Catalogs";
import { DashboardPage } from "./pages/Dashboard";
import { HelpButton } from "./help";
import { OperatorPage } from "./pages/Operator";
import { DriverPage } from "./pages/Driver";
import { FleetPage } from "./pages/Fleet";
import { TrackPage } from "./pages/Track";
import { registerPwa } from "../shared/install";
import { PlanPage } from "./pages/Plan";
import { ReportsPage } from "./pages/Reports";
import { SchedulePage } from "./pages/Schedule";
import { ShiftPage } from "./pages/Shift";
import { StockPage } from "./pages/Stock";
import { LabPage } from "./pages/Lab";
import { WeekPage } from "./pages/Week";

const NAV: (NavItem & { section: string })[] = [
  { to: "/schedule", label: "График отгрузок", icon: "schedule", mobile: true, section: "schedule" },
  { to: "/week", label: "Неделя", icon: "week", section: "week" },
  { to: "/fleet", label: "Машины", icon: "fleet", mobile: true, section: "fleet" },
  { to: "/shift", label: "Смена и задачи", icon: "shift", mobile: true, section: "shift" },
  { to: "/stock", label: "Склад сырья", icon: "stock", section: "stock" },
  { to: "/lab", label: "Лаборатория", icon: "lab", mobile: true, section: "lab" },
  { to: "/operator", label: "Экран оператора", icon: "operator", mobile: true, section: "operator" },
  { to: "/dashboard", label: "Дашборд", icon: "dashboard", mobile: true, section: "dashboard" },
  { to: "/plan", label: "План поставок", icon: "plan", mobile: true, section: "plan" },
  { to: "/reports", label: "Отчёты", icon: "report", section: "reports" },
  { to: "/catalogs", label: "Справочники", icon: "catalog", section: "catalogs" },
  { to: "/admin", label: "Администрирование", icon: "admin", section: "admin" },
];

function navFor(role: Me["role"]): NavItem[] {
  return NAV.filter((n) => canOpen(role, n.section)).map(({ section: _s, ...n }) => n);
}

function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [refs, setRefs] = useState<Refs | null>(null);
  const [needLogin, setNeedLogin] = useState(!api.token);
  const [queued, setQueued] = useState(0);
  const online = useOnline(live);
  const route = useRoute();

  const loadRefs = useCallback(async () => {
    const [plants, units, grades, reasons] = await Promise.all([
      api.get("/plants"), api.get("/units"), api.get("/grades", { limit: 500 }), api.get("/delay-reasons"),
    ]);
    setRefs({ plants: plants.items, units: units.items, grades: grades.items, delayReasons: reasons.items });
  }, []);

  const start = useCallback(async () => {
    try {
      const m: Me = await api.get("/me");
      setTimezone(m.settings.timezone);
      setMe(m);
      await loadRefs();
      live.start();
      setNeedLogin(false);
    } catch {
      setNeedLogin(true);
    }
  }, [loadRefs]);

  useEffect(() => {
    api.onUnauthorized = () => { api.logout(); live.stop(); setMe(null); setNeedLogin(true); };
    if (api.token) start();
    return queue.subscribe((items) => setQueued(items.length));
  }, [start]);

  // страница отслеживания для клиента — без входа (Э-15)
  if (route.parts[0] === "t" && route.parts[1]) return <TrackPage token={route.parts[1]} />;
  if (needLogin) {
    return <LoginScreen api={api} title="ERP «Бетон»" subtitle="Заявки, график отгрузок, РБУ, водители" allowPin allowDriver onDone={start} />;
  }
  if (!me || !refs) return <Loading />;
  if (route.path === "/" || route.path === "") {
    navigate(home(me.role));
    return null;
  }
  // раздел, не положенный роли, не открывается и по прямому адресу — возвращаем на стартовый экран
  const section = route.parts[0] || "schedule";
  if (!canOpen(me.role, section)) {
    navigate(home(me.role));
    return null;
  }
  const logout = () => { api.logout(); live.stop(); setMe(null); setNeedLogin(true); location.hash = "/"; };
  const page = (() => {
    switch (route.parts[0]) {
      case "schedule": return <SchedulePage />;
      case "week": return <WeekPage />;
      case "fleet": return <FleetPage />;
      case "shift": return <ShiftPage />;
      case "stock": return <StockPage />;
      case "lab": return <LabPage />;
      case "dashboard": return <DashboardPage />;
      case "plan": return <PlanPage />;
      case "reports": return <ReportsPage />;
      case "catalogs": return <CatalogsPage name={route.parts[1]} />;
      case "admin": return <AdminPage />;
      default: return <SchedulePage />;
    }
  })();
  return (
    <Ctx.Provider value={{ me, refs, reloadRefs: loadRefs }}>
      {route.parts[0] === "operator" ? (
        <OperatorPage online={online} queued={queued} onLogout={logout} />
      ) : route.parts[0] === "driver" ? (
        <DriverPage online={online} queued={queued} onLogout={logout} />
      ) : (
        <Shell brand="ERP «Бетон»" nav={navFor(me.role)} user={me.full_name} onLogout={logout} online={online} queued={queued}>
          {page}
        </Shell>
      )}
      <HelpButton />
    </Ctx.Provider>
  );
}

registerPwa();

createRoot(document.getElementById("root")!).render(
  <ToastProvider>
    <App />
  </ToastProvider>,
);
